import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Lock, Mail, Phone, MapPin,
  BookOpen, ArrowRight, ShieldCheck, Globe,
  Sparkles, GraduationCap, Eye, EyeOff, Sun, Moon
} from "lucide-react";
import { PATHS } from "./config/paths";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { INDIAN_STATES, STATE_DISTRICT_MAP } from "./utils/indiaData";
import { toast } from "sonner";
import API from "./api/api01";
import { ENDPOINTS } from "./config/endpoints";

// 🌌 Particle Network Component
const ParticleNetwork = ({ isDark }: { isDark: boolean }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let particles: any[] = [];
    const particleCount = 100;
    const connectionDistance = 140;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", resize);
    resize();
    class Particle {
      x: number; y: number; vx: number; vy: number; radius: number;
      constructor() {
        this.x = Math.random() * canvas!.width;
        this.y = Math.random() * canvas!.height;
        this.vx = (Math.random() - 0.5) * 0.6;
        this.vy = (Math.random() - 0.5) * 0.6;
        this.radius = Math.random() * 1.2 + 0.5;
      }
      update() {
        this.x += this.vx; this.y += this.vy;
        if (this.x < 0 || this.x > canvas!.width) this.vx = -this.vx;
        if (this.y < 0 || this.y > canvas!.height) this.vy = -this.vy;
      }
      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = isDark ? "rgba(100, 150, 255, 0.3)" : "rgba(37, 99, 235, 0.2)";
        ctx.fill();
      }
    }
    for (let i = 0; i < particleCount; i++) particles.push(new Particle());
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particles.length; i++) {
        particles[i].update(); particles[i].draw();
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            ctx.beginPath(); ctx.moveTo(particles[i].x, particles[i].y); ctx.lineTo(particles[j].x, particles[j].y);
            let op = 1 - dist / connectionDistance;
            ctx.strokeStyle = isDark ? `rgba(100, 150, 255, ${op * 0.1})` : `rgba(37, 99, 235, ${op * 0.15})`;
            ctx.lineWidth = 0.6; ctx.stroke();
          }
        }
      }
      animationFrameId = requestAnimationFrame(draw);
    };
    draw();
    return () => { window.removeEventListener("resize", resize); cancelAnimationFrame(animationFrameId); };
  }, [isDark]);
  return <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-0" />;
};

export function LoginPage({ onLogin }: { onLogin?: (data: any) => Promise<void> }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [isDark, setIsDark] = useState(() => localStorage.getItem("theme") !== "light");
  const [showPassword, setShowPassword] = useState(false);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const [showSuccess, setShowSuccess] = useState(false);

  // Dynamic Data States
  const [departments, setDepartments] = useState<any[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [subStatus, setSubStatus] = useState("");

  // Auth States
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  // Admission States
  const [admissionData, setAdmissionData] = useState({
    name: "",
    email: "",
    phone: "",
    state: "",
    district: "",
    program: "", // Department Name
    course: "",  // Course Name
    abcId: "",
  });

  // Fetch Dynamic Data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deptRes, courseRes] = await Promise.all([
          API.get(ENDPOINTS.ACADEMICS.DEPARTMENTS),
          API.get(ENDPOINTS.ACADEMICS.COURSES)
        ]);
        setDepartments(deptRes.data || []);
        setCourses(courseRes.data || []);
      } catch (error) {
        console.error("Failed to fetch academics data:", error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem("theme", isDark ? "dark" : "light");
  }, [isDark]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");

    try {
      if (onLogin) {
        await onLogin({ username, password });
      } else {
        const response = await API.post(ENDPOINTS.AUTH.LOGIN, { username, password });
        localStorage.setItem("user", JSON.stringify(response.data));
      }

      setStatus("success");
      toast.success("Authentication successful. Welcome back.");

      // The navigation is handled by App.tsx when currentUser changes, 
      // but we can add a fallback or let App.tsx handle it.
      // App.tsx's handleLogin sets the user after 2.2s.
    } catch (err: any) {
      setStatus("idle");
      const errorMsg = err.response?.data?.detail || "Invalid credentials or server error.";
      toast.error(errorMsg);
    }
  };

  const handleAdmissionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic Form Validation
    const { name, email, phone, state, district, program, course } = admissionData;

    if (name.trim().length < 3) {
      toast.error("Please enter a valid full name (min 3 characters).");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!/^\d{10}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }

    if (!state || !district || !program || !course) {
      toast.error("Please fill all required fields marked with (*).");
      return;
    }

    setShowConfirm(true);
  };

  const confirmFinalSubmit = async () => {
    setShowConfirm(false);
    setStatus("loading");
    setSubStatus("Initializing Secure Handshake...");

    try {
      // Small delay to show the first state
      await new Promise(resolve => setTimeout(resolve, 800));
      setSubStatus("Transmitting Application Data...");

      // Create lead in CRM
      const response = await API.post(ENDPOINTS.PUBLIC.REGISTER, {
        name: admissionData.name,
        email: admissionData.email,
        phone: admissionData.phone,
        city: admissionData.district,
        campus: admissionData.state,
        course_interested: `${admissionData.program} - ${admissionData.course}`,
        notes: `ABC ID: ${admissionData.abcId}`,
        registration_type: 'application'
      });

      if (response.data.status === "success") {
        setSubStatus("Backend is taking request...");
        await new Promise(resolve => setTimeout(resolve, 1000));

        setSubStatus("Handshake Successful. Synced.");
        await new Promise(resolve => setTimeout(resolve, 600));

        setStatus("success");
        toast.success("Application successfully synchronized with Backend!");

        setTimeout(() => {
          setStatus("idle");
          setShowSuccess(true);
          // Clear Fields
          setAdmissionData({
            name: "", email: "", phone: "",
            state: "", district: "",
            program: "", course: "", abcId: ""
          });
        }, 800);
      }
    } catch (err) {
      setStatus("idle");
      setSubStatus("");
      toast.error("Submission failed. Connection refused or invalid data.");
    }
  };

  const inputStyles = `w-full ${isDark ? 'bg-white/[0.03] border-white/10 focus:bg-white/[0.05] focus:border-blue-500/50 text-white' : 'bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500/50 text-slate-900'} border rounded-2xl py-3.5 pl-12 pr-4 outline-none transition-all placeholder:opacity-40 placeholder:font-medium text-sm focus:ring-4 ${isDark ? 'focus:ring-blue-500/10' : 'focus:ring-blue-500/5'}`;
  const selectTriggerStyles = `w-full h-[52px] ${isDark ? 'bg-white/[0.03] border-white/10 focus:bg-white/[0.05] focus:border-blue-500/50 text-white' : 'bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500/50 text-slate-900'} border rounded-2xl px-4 outline-none transition-all shadow-none text-sm font-medium focus:ring-4 ${isDark ? 'focus:ring-blue-500/10' : 'focus:ring-blue-500/5'}`;

  return (
    <div onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })} className={`min-h-screen w-full flex flex-col items-center justify-center p-6 lg:p-10 transition-colors duration-700 relative overflow-hidden ${isDark ? 'bg-[#05070a]' : 'bg-slate-50'}`}>

      {/* 🔮 Background Layering */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 100, 0], y: [0, 50, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[10%] -left-[10%] w-[50%] h-[50%] bg-blue-600/10 rounded-full blur-[120px]"
        />
        <motion.div
          animate={{ x: [0, -100, 0], y: [0, -50, 0], scale: [1.2, 1, 1.2] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-[10%] -right-[10%] w-[50%] h-[50%] bg-indigo-600/10 rounded-full blur-[120px]"
        />
        <div className={`absolute inset-0 opacity-[0.03]`} style={{ backgroundImage: `linear-gradient(${isDark ? '#ffffff' : '#000000'} 1px, transparent 1px), linear-gradient(90deg, ${isDark ? '#ffffff' : '#000000'} 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
        <div className="absolute inset-0" style={{ background: isDark ? `radial-gradient(800px circle at ${mouse.x}px ${mouse.y}px, rgba(59,130,246,0.06), transparent 40%)` : `radial-gradient(800px circle at ${mouse.x}px ${mouse.y}px, rgba(59,130,246,0.04), transparent 40%)` }} />
      </div>

      <ParticleNetwork isDark={isDark} />

      {/* 🏛 Navigation */}
      <header className="relative z-50 p-5 flex justify-end items-center max-w-[1100px] mx-auto w-full mb-2 h-20">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute left-5 top-0"
        >
          <img src="/logo12.png" alt="XeroAlpha" className="w-40 h-40 object-contain drop-shadow-2xl" />
        </motion.div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsDark(!isDark)}
          className={`p-3 rounded-2xl border transition-all duration-300 ${isDark ? "bg-white/5 border-white/10 hover:bg-white/10" : "bg-white border-slate-200 shadow-sm hover:shadow-md"}`}
        >
          {isDark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} className="text-blue-600" />}
        </motion.button>
      </header>

      <main className="w-full max-w-[1100px] flex flex-col lg:flex-row items-center gap-16 lg:gap-24 relative z-10">
        {/* 🏛️ FEATURE TILE (LEFT) */}
        <div className="hidden lg:flex lg:w-1/2 flex-col space-y-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className={`p-10 rounded-3xl border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-white/50 border-slate-200'} backdrop-blur-md relative overflow-hidden group`}
          >
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <Sparkles size={120} className="text-blue-600" />
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-600/10 border border-blue-600/20 text-blue-500 text-[10px] font-black uppercase tracking-[0.2em] mb-8">
              Portal Access System
            </div>

            <h2 className={`text-4xl lg:text-5xl font-bold tracking-tight leading-tight mb-8 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Establish Your <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-500">Global Identity</span>
            </h2>

            <p className={`text-lg font-medium opacity-60 leading-relaxed mb-10 max-w-md ${isDark ? 'text-white' : 'text-slate-600'}`}>
              Immersive gateway for the next generation of academic and professional excellence.
            </p>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-1">99.9%</div>
                <div className={`text-[10px] font-semibold uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-slate-400'}`}>System Uptime</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600 mb-1">12k+</div>
                <div className={`text-[10px] font-semibold uppercase tracking-widest ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Active Candidates</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`p-8 rounded-3xl border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-white/50 border-slate-200'} backdrop-blur-md`}
          >
            <div className="flex items-center gap-6">
              <div className="flex -space-x-4">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-12 h-12 rounded-full border-4 border-[#05070a] bg-gradient-to-br from-blue-600 to-indigo-600 shadow-xl" />)}
              </div>
              <div>
                <h4 className={`text-sm font-bold tracking-wide ${isDark ? 'text-white' : 'text-slate-900'}`}>Trusted Infrastructure</h4>
                <p className={`text-[11px] font-medium opacity-50 ${isDark ? 'text-white' : 'text-slate-500'}`}>Serving Institutions Worldwide</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* 🧊 FORM BOX (RIGHT) */}
        <div className="relative w-full lg:w-[480px]">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className={`relative z-10 w-full rounded-3xl border overflow-hidden group ${isDark ? 'bg-black/40 backdrop-blur-3xl border-white/10' : 'bg-white/80 backdrop-blur-2xl border-slate-200'}`}
          >
            {/* Box Header */}
            <div className={`p-12 pb-8 text-center border-b ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
              <div className="flex flex-col items-center gap-2">
                <span className="px-3 py-1 rounded-full bg-blue-600/10 text-blue-500 text-[10px] font-bold uppercase tracking-widest border border-blue-500/20 mb-4">Command Center</span>
                <h3 className="text-3xl font-bold tracking-tight">
                  {mode === "register" ? "Admission" : "Identity"}
                </h3>
              </div>

              {/* Tab Switcher */}
              <div className={`mt-8 p-1.5 rounded-xl flex items-center relative ${isDark ? 'bg-black/40' : 'bg-slate-100'}`}>
                <motion.div
                  className="absolute inset-y-1.5 rounded-lg bg-blue-600"
                  initial={false}
                  animate={{ x: mode === "register" ? "0%" : "100%", width: "calc(50% - 6px)" }}
                  transition={{ type: "spring", stiffness: 400, damping: 35 }}
                />
                <button
                  onClick={() => { setMode("register"); setStatus("idle"); setShowSuccess(false); }}
                  className={`flex-1 py-3 rounded-lg text-[11px] font-bold tracking-wider transition-colors relative z-10 ${mode === "register" ? 'text-white' : 'text-slate-500'}`}
                >
                  Register
                </button>
                <button
                  onClick={() => { setMode("login"); setStatus("idle"); setShowSuccess(false); }}
                  className={`flex-1 py-3 rounded-lg text-[11px] font-bold tracking-wider transition-colors relative z-10 ${mode === "login" ? 'text-white' : 'text-slate-500'}`}
                >
                  Login
                </button>
              </div>
            </div>

            {/* Box Body */}
            <motion.div layout className="p-12 pt-10 overflow-hidden relative min-h-[400px]">
              {/* ⚡ AUTH LOADING OVERLAY */}
              <AnimatePresence>
                {(status === "loading" || (status === "success" && !showSuccess)) && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={`absolute inset-0 z-50 ${isDark ? 'bg-black/80 text-white' : 'bg-white/90 text-slate-900'} backdrop-blur-xl flex flex-col items-center justify-center p-12 text-center`}
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-20 h-20 border-4 border-blue-600 border-t-transparent rounded-full mb-8"
                    />
                    <motion.div
                      initial={{ y: 10, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                    >
                      <h4 className="text-xl font-bold tracking-tight mb-2">
                        {status === "success" ? "Verified" : "Synchronizing"}
                      </h4>
                      <p className="text-[10px] font-bold tracking-widest text-blue-500 animate-pulse uppercase">
                        {subStatus || "Establishing Secure Session"}
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait" initial={false}>
                {showSuccess ? (
                  <motion.div
                    key="success-screen"
                    initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                    className="flex flex-col items-center text-center py-6"
                  >
                    <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6 border border-emerald-500/20 shadow-xl shadow-emerald-500/5">
                      <ShieldCheck size={40} className="text-emerald-500" />
                    </div>

                    <h4 className="text-2xl font-bold tracking-tight mb-4">Success!</h4>
                    <p className={`text-sm font-medium opacity-70 leading-relaxed mb-6 px-4 ${isDark ? 'text-white' : 'text-slate-600'}`}>
                      Thank you for choosing XeroAlpha. Your application has been successfully filled.
                    </p>

                    <div className={`w-full p-4 rounded-xl border mb-8 text-left ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
                      <p className="text-[11px] font-bold leading-relaxed opacity-60 uppercase tracking-widest text-center">
                        Our admission team will contact you shortly
                      </p>
                    </div>

                    <button
                      onClick={() => { setShowSuccess(false); setMode("login"); }}
                      className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold tracking-wide text-xs shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                    >
                      Back to Login <ArrowRight size={16} />
                    </button>
                  </motion.div>
                ) : mode === "register" ? (
                  <motion.form
                    key="reg"
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                    onSubmit={handleAdmissionSubmit} className="space-y-5"
                  >
                    <div className="max-h-[380px] overflow-y-auto pr-2 custom-scrollbar space-y-5">
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={18} />
                        <input required placeholder="Candidate Full Name" className={inputStyles} value={admissionData.name} onChange={e => setAdmissionData({ ...admissionData, name: e.target.value })} />
                      </div>

                      <div className="grid grid-cols-2 gap-5">
                        <div className="relative group">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={18} />
                          <input required type="email" placeholder="Email Address" className={inputStyles} value={admissionData.email} onChange={e => setAdmissionData({ ...admissionData, email: e.target.value })} />
                        </div>
                        <div className="relative group">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={18} />
                          <input
                            required
                            type="tel"
                            pattern="\d{10}"
                            maxLength={10}
                            placeholder="Phone Number (10 Digit)"
                            className={inputStyles}
                            value={admissionData.phone}
                            onChange={e => setAdmissionData({ ...admissionData, phone: e.target.value.replace(/\D/g, '') })}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-5">
                        <Select value={admissionData.state} onValueChange={v => setAdmissionData({ ...admissionData, state: v, district: "" })}>
                          <SelectTrigger className={selectTriggerStyles}><SelectValue placeholder="Select State*" /></SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {INDIAN_STATES.map(state => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={admissionData.district} onValueChange={v => setAdmissionData({ ...admissionData, district: v })} disabled={!admissionData.state}>
                          <SelectTrigger className={selectTriggerStyles}><SelectValue placeholder="Select District*" /></SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {admissionData.state && STATE_DISTRICT_MAP[admissionData.state]?.map(dist => (
                              <SelectItem key={dist} value={dist}>{dist}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-5">
                        <Select value={admissionData.program} onValueChange={v => setAdmissionData({ ...admissionData, program: v })}>
                          <SelectTrigger className={selectTriggerStyles}><SelectValue placeholder="Select Program*" /></SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {departments.map(dept => (
                              <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select value={admissionData.course} onValueChange={v => setAdmissionData({ ...admissionData, course: v })}>
                          <SelectTrigger className={selectTriggerStyles}><SelectValue placeholder="Select Course*" /></SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {courses
                              .filter(c => !admissionData.program || c.department_name === admissionData.program || c.department === departments.find(d => d.name === admissionData.program)?.id)
                              .map(course => (
                                <SelectItem key={course.id} value={course.name}>{course.name}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="relative group">
                        <input placeholder="Academic ABC ID (Optional)" className={inputStyles.replace('pl-12', 'px-5')} value={admissionData.abcId} onChange={e => setAdmissionData({ ...admissionData, abcId: e.target.value })} />
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                      type="submit" disabled={status === "loading"}
                      className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-bold tracking-wide text-sm flex justify-center items-center gap-3 mt-8 hover:bg-blue-700 transition-all"
                    >
                      {status === "loading" ? "Processing..." : <>Finalize Application <ArrowRight size={18} /></>}
                    </motion.button>
                  </motion.form>
                ) : (
                  <motion.form
                    key="login"
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
                    onSubmit={handleLogin} className="space-y-8"
                  >
                    <div className="relative group">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={20} />
                      <input required value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Username" className={inputStyles} />
                    </div>
                    <div className="relative group">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600 transition-colors" size={20} />
                      <input required type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputStyles} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors">
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <div className="flex justify-end px-2">
                      <button type="button" onClick={() => navigate(PATHS.FORGOT_PASSWORD)} className="text-[11px] font-bold text-blue-600 hover:text-indigo-500 transition-colors">
                        Recover Account Portal
                      </button>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                      type="submit" disabled={status === "loading"}
                      className={`w-full py-3.5 rounded-xl font-bold tracking-wide text-sm transition-all flex justify-center items-center gap-3 ${status === 'loading' ? 'bg-blue-600/50 cursor-not-allowed' : status === 'success' ? 'bg-emerald-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                      {status === "loading" ? "Verifying..." : status === "success" ? "Authorized" : <>Login <ArrowRight size={20} /></>}
                    </motion.button>
                  </motion.form>
                )}
              </AnimatePresence>

              {/* 🛡️ CONFIRMATION DIALOG */}
              <AnimatePresence>
                {showConfirm && (
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 z-[60] bg-black/60 backdrop-blur-xl flex items-center justify-center p-8"
                  >
                    <motion.div
                      initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }}
                      className={`w-full max-w-[340px] rounded-2xl p-10 text-center border shadow-2xl ${isDark ? 'bg-[#0a0c10] border-white/10' : 'bg-white border-slate-200'}`}
                    >
                      <div className="w-14 h-14 bg-blue-600/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-blue-500/20">
                        <GraduationCap size={28} className="text-blue-600" />
                      </div>
                      <h4 className="text-lg font-bold tracking-tight mb-2">Review Application</h4>
                      <p className="text-[11px] font-medium opacity-60 leading-relaxed mb-8">
                        Verify your details before finalization
                      </p>

                      <div className="space-y-3">
                        <button
                          onClick={confirmFinalSubmit}
                          className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-bold tracking-wide text-xs shadow-lg shadow-blue-600/30 hover:bg-blue-700 transition-all active:scale-95"
                        >
                          Proceed
                        </button>
                        <button
                          onClick={() => setShowConfirm(false)}
                          className={`w-full py-3.5 rounded-xl font-bold tracking-wide text-xs transition-all border ${isDark ? 'border-white/10 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}
                        >
                          Modify Details
                        </button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </div>
      </main>

      <footer className="relative z-50 mt-10 p-8 text-center">
        <p className={`text-[11px] font-medium tracking-widest ${isDark ? 'text-white/20' : 'text-slate-400'}`}>
          © 2026 XeroAlpha Network • Intelligent Academic Core
        </p>
      </footer>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #2563eb; border-radius: 10px; }
      `}</style>
    </div>
  );
}